/**
 * Единая точка записи exchange-ответа (states + optional changed_data) и imperative apply.
 *
 * Порядок: merge states → store → processStates (один раз) → strip одноразовых custom_properties.
 */
import type { ElementState } from '@/common/composables/usePageData'
import { CustomPropertiesKeys } from '@/common/composables/usePageData'
import { mergeExchangeStatesForPageUpdate } from '@/common/utils/normalizeExchangeStates'
import {
  resolveStateUpdateStoreKey,
  type ResolveStateUpdateStoreKeyOptions,
} from '@/common/utils/resolvePageStoreEntry'
import type { AppStore } from '@/stores/app'
import type { StateData } from '@/services/action/types'

/** custom_properties, которые нельзя хранить в jsResult.states (одноразовые команды). */
const ONE_SHOT_CUSTOM_PROPERTY_NAMES = new Set<string>([
  CustomPropertiesKeys.APPEND_BYTES,
  CustomPropertiesKeys.APPEND_FILE_COPIES,
])

export type ProcessStatesFn = (
  formId: string,
  dataStates?: ElementState[],
  changedData?: unknown,
  contextFormId?: string | number | null,
) => Promise<void>

export type ApplyIncomingExchangeStatesOptions = {
  appStore: AppStore
  slotId: string
  registryFormId?: string | number | null
  incomingStates: StateData[] | ElementState[]
  changedData?: unknown
  processStates?: ProcessStatesFn
  setPageDataSource: string
  resolveStoreKey?: Omit<ResolveStateUpdateStoreKeyOptions, 'registryFormId' | 'formId'>
}

function resolveCommitStoreKey(
  appStore: AppStore,
  slotId: string,
  registryId: string,
  resolveStoreKey?: Omit<ResolveStateUpdateStoreKeyOptions, 'registryFormId' | 'formId'>,
): string {
  const pages = appStore.pagesData as Record<string, unknown>
  return resolveStateUpdateStoreKey(pages as Record<string, any>, {
    originSlotId: slotId,
    pageDataSlot: slotId,
    registryFormId: registryId,
    formId: registryId,
    currentPageId: appStore.currentPageId,
    topDialogId: appStore.dialogIds?.length
      ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
      : '',
    ...resolveStoreKey,
  })
}

/**
 * Применяет incoming states после того, как pageData уже записан в store.
 * Используется, когда writer сам мержит uniData/changed_data/sessionId.
 */
export async function applyIncomingExchangeStates(
  options: ApplyIncomingExchangeStatesOptions,
): Promise<boolean> {
  const {
    appStore,
    slotId,
    registryFormId,
    incomingStates,
    changedData,
    processStates,
    setPageDataSource,
    resolveStoreKey,
  } = options

  const stateList = Array.isArray(incomingStates) ? incomingStates : []
  if (!stateList.length || typeof processStates !== 'function') {
    return false
  }

  const registryId = registryFormId != null ? String(registryFormId) : slotId
  await processStates(slotId, stateList as ElementState[], changedData, registryFormId)

  const storeKey = resolveCommitStoreKey(appStore, slotId, registryId, resolveStoreKey)
  if (!storeKey) return true

  const freshPageData = (appStore.pagesData as Record<string, any>)[storeKey]
  const persistedStates = freshPageData?.jsResult?.states
  if (Array.isArray(persistedStates) && persistedStates.length > 0) {
    const { states: stripped, changed: strippedChanged } =
      stripOneShotCustomPropertiesFromStates(persistedStates as ElementState[])
    if (strippedChanged) {
      appStore.setPageData(
        storeKey,
        {
          ...freshPageData,
          jsResult: {
            ...freshPageData.jsResult,
            states: stripped,
          },
        },
        undefined,
        undefined,
        `${setPageDataSource}.stripOneShotStates`,
      )
    }
  }

  return true
}

export type CommitExchangeResultOptions = {
  appStore: AppStore
  slotId: string
  registryFormId?: string | number | null
  states: StateData[] | ElementState[]
  changedData?: unknown
  processStates?: ProcessStatesFn
  setPageDataSource: string
  resolveStoreKey?: Omit<ResolveStateUpdateStoreKeyOptions, 'registryFormId' | 'formId'>
  /** Ответ платформы для mergeExchangeStatesForPageUpdate (корень или jsResult). */
  responseForStateMerge?: Record<string, unknown> | null
  /** Доп. поля jsResult поверх merge states (event_listeners, changed_data, …). */
  jsResultExtras?: Record<string, unknown>
}

export function stripOneShotCustomPropertiesFromStates<T extends ElementState>(
  states: T[],
): { states: T[]; changed: boolean } {
  if (!Array.isArray(states) || states.length === 0) {
    return { states, changed: false }
  }
  let changed = false
  const next = states.map((state) => {
    const props = state.custom_properties
    if (!Array.isArray(props) || props.length === 0) return state
    const filtered = props.filter(
      (prop) => !ONE_SHOT_CUSTOM_PROPERTY_NAMES.has(String(prop?.property_name ?? '')),
    )
    if (filtered.length === props.length) return state
    changed = true
    if (filtered.length === 0) {
      const { custom_properties: _removed, ...rest } = state as T & {
        custom_properties?: ElementState['custom_properties']
      }
      return rest as T
    }
    return { ...state, custom_properties: filtered }
  })
  return { states: changed ? next : states, changed }
}

/**
 * Записывает states в pagesData и один раз применяет processStates.
 */
export async function commitExchangeResult(options: CommitExchangeResultOptions): Promise<boolean> {
  const {
    appStore,
    slotId,
    registryFormId,
    states,
    changedData,
    processStates,
    setPageDataSource,
    resolveStoreKey,
    responseForStateMerge,
    jsResultExtras,
  } = options

  const stateList = Array.isArray(states) ? states : []
  const hasStates = stateList.length > 0
  const hasJsExtras = jsResultExtras != null && Object.keys(jsResultExtras).length > 0
  if (!hasStates && !hasJsExtras) {
    return false
  }

  const registryId = registryFormId != null ? String(registryFormId) : slotId
  const storeKey = resolveCommitStoreKey(appStore, slotId, registryId, resolveStoreKey)
  if (!storeKey) return false

  const pages = appStore.pagesData as Record<string, unknown>

  const pageData = (pages as Record<string, any>)[storeKey]
  if (!pageData) return false

  const mergeSource =
    responseForStateMerge ??
    (hasStates ? ({ jsResult: { states: stateList } } as Record<string, unknown>) : null)

  const mergedStates = hasStates
    ? mergeExchangeStatesForPageUpdate(mergeSource, pageData)
    : pageData.jsResult?.states

  const updatedPageData = {
    ...pageData,
    jsResult: {
      ...pageData.jsResult,
      ...(responseForStateMerge?.jsResult &&
      typeof responseForStateMerge.jsResult === 'object' &&
      !Array.isArray(responseForStateMerge.jsResult)
        ? responseForStateMerge.jsResult
        : {}),
      ...(jsResultExtras ?? {}),
      ...(hasStates ? { states: mergedStates } : {}),
    },
  }

  appStore.setPageData(storeKey, updatedPageData, undefined, undefined, setPageDataSource)

  if (hasStates) {
    await applyIncomingExchangeStates({
      appStore,
      slotId,
      registryFormId,
      incomingStates: stateList,
      changedData,
      processStates,
      setPageDataSource,
      resolveStoreKey,
    })
  }

  return true
}
